import React, { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { functionsEurope } from '../firebase';
import ModalBasic from './ModalBasic';
import { notifySuccess } from '../utils/notify';

const claimSlugCallable = httpsCallable(functionsEurope, 'claimSlug');
const checkSlugAvailableCallable = httpsCallable(functionsEurope, 'checkSlugAvailable');

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
const MAX_SUGGESTION_ATTEMPTS = 20;

function clientValidate(slug) {
  if (!slug) return { ok: false, reason: '' };
  if (slug.length < 3) return { ok: false, reason: 'At least 3 characters.' };
  if (slug.length > 40) return { ok: false, reason: 'Max 40 characters.' };
  if (!SLUG_REGEX.test(slug)) return { ok: false, reason: 'Lowercase letters, numbers, hyphens. No leading/trailing hyphen.' };
  if (/^\d+$/.test(slug)) return { ok: false, reason: 'Cannot be all numbers.' };
  return { ok: true };
}

function clampSlug(slug, maxLength = 40) {
  return slug.slice(0, maxLength).replace(/-+$/g, '');
}

function slugifySuggestion(value) {
  const input = typeof value === 'string' ? value : '';
  const normalized = typeof input.normalize === 'function' ? input.normalize('NFKD') : input;
  let slug = normalized
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (!slug) slug = 'game';
  if (slug.length < 3) slug = `${slug}-game`;
  if (/^\d+$/.test(slug)) slug = `game-${slug}`;

  slug = clampSlug(slug);
  return clientValidate(slug).ok ? slug : 'game';
}

function buildSuggestedSlug(baseSlug, attempt) {
  if (attempt === 0) return baseSlug;
  const suffix = `-${attempt + 1}`;
  const base = clampSlug(baseSlug, 40 - suffix.length) || 'game';
  return `${base}${suffix}`;
}

export default function SlugEditor({ campaignId, currentSlug, onSaved, modalOpen = false, setModalOpen = () => {}, suggestionSource = '' }) {
  const [value, setValue] = useState(currentSlug || '');
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | checking | available | taken | invalid | error
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionRequestRef = useRef(0);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.smilesflow.com';
  const displayOrigin = origin.replace(/^https?:\/\//, '');
  const savedSlug = currentSlug || '';
  const trimmedValue = value.trim();
  const hasChanges = trimmedValue && trimmedValue !== savedSlug;
  const canSave = hasChanges && !saving && !['taken', 'invalid', 'checking'].includes(status.state);
  const previewSlug = trimmedValue || 'your-brand';

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (!modalOpen || !inputRef.current) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 120);
    return () => clearTimeout(timer);
  }, [modalOpen]);

  const runAvailabilityCheck = useCallback(async (slug) => {
    try {
      const { data } = await checkSlugAvailableCallable({ slug, campaignId });
      if (data.available) {
        setStatus({ state: 'available', message: 'Available' });
      } else {
        setStatus({ state: 'taken', message: data.reason || 'Taken' });
      }
    } catch (err) {
      setStatus({ state: 'error', message: 'Could not check availability.' });
    }
  }, [campaignId]);

  const findAvailableSuggestion = useCallback(async (baseSlug, requestId) => {
    let lastReason = 'Try a different link.';
    for (let attempt = 0; attempt < MAX_SUGGESTION_ATTEMPTS; attempt += 1) {
      const slug = buildSuggestedSlug(baseSlug, attempt);
      const local = clientValidate(slug);
      if (!local.ok) {
        lastReason = local.reason;
        continue;
      }

      const { data } = await checkSlugAvailableCallable({ slug, campaignId });
      if (suggestionRequestRef.current !== requestId) return null;
      if (data.available) {
        return {
          slug,
          status: { state: 'available', message: 'Suggested from title' },
        };
      }
      lastReason = data.reason || lastReason;
    }

    return {
      slug: baseSlug,
      status: { state: 'taken', message: lastReason },
    };
  }, [campaignId]);

  useEffect(() => {
    if (!modalOpen) {
      suggestionRequestRef.current += 1;
      return undefined;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    const requestId = suggestionRequestRef.current + 1;
    suggestionRequestRef.current = requestId;

    if (savedSlug) {
      setValue(savedSlug);
      setStatus({ state: 'available', message: 'Current link' });
      return undefined;
    }

    const suggestedSlug = slugifySuggestion(suggestionSource);
    setValue(suggestedSlug);
    setStatus({ state: 'checking', message: 'Finding available link...' });

    findAvailableSuggestion(suggestedSlug, requestId)
      .then((result) => {
        if (!result || suggestionRequestRef.current !== requestId) return;
        setValue(result.slug);
        setStatus(result.status);
      })
      .catch(() => {
        if (suggestionRequestRef.current !== requestId) return;
        setStatus({ state: 'error', message: 'Could not check suggestion.' });
      });

    return () => {
      if (suggestionRequestRef.current === requestId) {
        suggestionRequestRef.current += 1;
      }
    };
  }, [modalOpen, savedSlug, campaignId, suggestionSource, findAvailableSuggestion]);

  const handleChange = (e) => {
    suggestionRequestRef.current += 1;
    const raw = e.target.value.toLowerCase().replace(/\s+/g, '-');
    setValue(raw);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!raw) {
      setStatus({ state: 'idle', message: '' });
      return;
    }
    const local = clientValidate(raw);
    if (!local.ok) {
      setStatus({ state: 'invalid', message: local.reason });
      return;
    }
    if (raw === savedSlug) {
      setStatus({ state: 'available', message: 'Current link' });
      return;
    }
    setStatus({ state: 'checking', message: 'Checking...' });
    debounceRef.current = setTimeout(() => runAvailabilityCheck(raw), 400);
  };

  const handleSave = async () => {
    const slug = value.trim();
    const local = clientValidate(slug);
    if (!local.ok) {
      setStatus({ state: 'invalid', message: local.reason });
      return;
    }
    if (slug === savedSlug) {
      setModalOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await claimSlugCallable({ campaignId, slug });
      notifySuccess('Custom link saved');
      setStatus({ state: 'idle', message: '' });
      onSaved?.(data.slug);
      setModalOpen(false);
    } catch (err) {
      const message = err?.message || 'Could not save custom link.';
      setStatus({ state: 'taken', message });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (canSave) handleSave();
  };

  const statusMeta = {
    available: {
      color: 'text-emerald-400',
      icon: CheckCircleIcon,
      label: status.message || 'Available',
    },
    invalid: {
      color: 'text-amber-400',
      icon: ExclamationTriangleIcon,
      label: status.message,
    },
    taken: {
      color: 'text-red-400',
      icon: ExclamationTriangleIcon,
      label: status.message,
    },
    error: {
      color: 'text-red-400',
      icon: ExclamationTriangleIcon,
      label: status.message,
    },
    checking: {
      color: 'gch-text-muted',
      icon: ArrowPathIcon,
      label: status.message || 'Checking...',
    },
    idle: {
      color: 'gch-text-muted',
      icon: null,
      label: status.message,
    },
  }[status.state];
  const StatusIcon = statusMeta.icon;

  return (
    <ModalBasic
      id="custom-link-modal"
      title="Custom play link"
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
    >
      <div className="p-5 space-y-4">
        <div className="flex gap-3 rounded-xl border border-[var(--admin-border)] bg-white/5 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--admin-accent-light)]">
            <LinkIcon className="h-4 w-4 text-[var(--admin-accent)]" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold gch-text-primary">Create a shorter branded URL.</p>
            <p className="mt-1 text-xs gch-text-secondary">
              This updates the play link used by copy, QR download, and social sharing.
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="custom-slug-input" className="block text-xs font-semibold uppercase tracking-wider gch-text-muted mb-2">
            Short link
          </label>
          <div className="overflow-hidden rounded-xl border border-[var(--admin-border)] gch-input-ghost focus-within:border-[var(--admin-accent)] focus-within:ring-2 focus-within:ring-[var(--admin-accent)]/20">
            <div className="border-b border-[var(--admin-border)] px-3 py-2">
              <p className="truncate text-[11px] font-medium gch-text-muted">{displayOrigin}/p/</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-3">
              <input
                id="custom-slug-input"
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="your-brand"
                maxLength={40}
                className="min-w-0 flex-1 bg-transparent text-lg font-semibold gch-text-primary outline-none placeholder:gch-text-muted"
                aria-label="Custom link slug"
              />
              {StatusIcon && (
                <StatusIcon
                  className={`h-4 w-4 shrink-0 ${statusMeta.color} ${status.state === 'checking' ? 'animate-spin' : ''}`}
                  aria-hidden
                />
              )}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className={`min-w-0 flex-1 truncate text-xs ${statusMeta.color}`}>
              {statusMeta.label || 'Lowercase letters, numbers, hyphens. 3-40 characters.'}
            </p>
            <p className="shrink-0 text-xs gch-text-muted">{trimmedValue.length}/40</p>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--admin-border)] bg-black/10 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider gch-text-muted">Preview</p>
          <p className="mt-1 truncate text-sm font-semibold gch-text-primary">{displayOrigin}/p/{previewSlug}</p>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className="btn-admin-outline btn-admin-md flex-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-admin-primary btn-admin-md flex-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save link'}
          </button>
        </div>
      </div>
    </ModalBasic>
  );
}
